import { useRef, useCallback, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Microphone, ArrowUp, Lightning, ArrowCounterClockwise, Stop } from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import { APP_NAME } from "../../lib/brand";
import { springSnappy } from "../../lib/motion-config";
import { useMentions, type MentionableFile } from "../../hooks/useMentions";
import { MentionDropdown } from "./MentionUI";
import { RichChatInput, type RichChatInputHandle } from "../../components/ai/RichChatInput";

export type { MentionableFile };

interface ChatInputProps {
  onSend: (message: string, mentionedFileIds?: string[]) => void;
  isProcessing: boolean;
  placeholder?: string;
  onCancel?: () => void;
  files?: MentionableFile[];
  activeFileId?: string;
}

const spring = springSnappy;

const QUICK_ACTIONS = [
  { id: "fix-errors", label: "Fix errors", icon: Lightning, message: "Fix the compilation errors in my document." },
  { id: "improve", label: "Improve writing", icon: ArrowCounterClockwise, message: "Review my document and suggest improvements to clarity and style." },
];

export function ChatInput({ onSend, isProcessing, placeholder, onCancel, files, activeFileId }: ChatInputProps) {
  const inputRef = useRef<RichChatInputHandle>(null);
  const [hasText, setHasText] = useState(false);

  const mentions = useMentions({ files, activeFileId });

  // ─── Select file from dropdown → insert inline chip ───

  const handleSelectFile = useCallback(
    (file: MentionableFile) => {
      inputRef.current?.insertChip(file);
      mentions.addMention(file);
    },
    [mentions],
  );

  // ─── Send ───

  const handleSend = useCallback(() => {
    const content = inputRef.current?.getContent();
    if (!content?.text || isProcessing) return;
    onSend(content.text, content.fileIds ?? mentions.getMentionedFileIds());
    inputRef.current?.clear();
    mentions.resetMentions();
  }, [isProcessing, onSend, mentions]);

  // ─── Keyboard routing ───

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Mention dropdown navigation
      if (mentions.showMentionMenu && mentions.filteredFiles.length > 0) {
        if (e.key === "Enter" || e.key === "Tab") {
          e.preventDefault();
          const file = mentions.getHighlightedFile();
          if (file) handleSelectFile(file);
          return;
        }
        if (mentions.handleMentionKeyDown(e)) return;
      }

      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
      if (e.key === "Escape" && onCancel) {
        e.preventDefault();
        onCancel();
      }
    },
    [handleSend, onCancel, mentions, handleSelectFile],
  );

  // ─── Mention trigger from RichChatInput ───

  const handleMentionTrigger = useCallback(
    (filter: string | null) => {
      mentions.setMentionTrigger(filter);
    },
    [mentions],
  );

  // ─── Chip lifecycle sync ───

  const handleChipRemoved = useCallback(
    (fileId: string) => {
      mentions.removeMention(fileId);
    },
    [mentions],
  );

  return (
    <div className="border-t border-[var(--border-hairline)] p-2">
      <AnimatePresence>
        {!hasText && !isProcessing && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.15 }}
            className="flex gap-1.5 mb-2 flex-wrap overflow-hidden"
          >
            {QUICK_ACTIONS.map((action) => (
              <motion.button
                key={action.id}
                onClick={() => onSend(action.message)}
                disabled={isProcessing}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                transition={spring}
                className={cn(
                  "flex items-center gap-1 rounded-[var(--radius-sm)] px-2 py-1 text-[10px] font-medium",
                  "bg-[var(--bg-surface-hover)] text-[var(--text-muted)]",
                  "hover:text-[var(--text-primary)]",
                  "border border-[var(--border-hairline)]",
                  "transition-colors disabled:opacity-50",
                )}
              >
                <action.icon size={10} weight="bold" />
                {action.label}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative flex items-end gap-1.5">
        <div className="relative flex-1 min-w-0">
          <RichChatInput
            ref={inputRef}
            onKeyDown={handleKeyDown}
            onMentionTrigger={handleMentionTrigger}
            onChipRemoved={handleChipRemoved}
            onContentChange={setHasText}
            placeholder={placeholder || `Ask ${APP_NAME} anything...`}
            disabled={isProcessing}
            className={cn(
              "w-full rounded-[var(--radius-md)] border border-[var(--border-subtle)]",
              "bg-[var(--bg-surface)] px-3 py-2 text-xs text-[var(--text-primary)]",
              "transition-colors",
              isProcessing && "opacity-50",
            )}
            maxHeight={120}
          />

          <MentionDropdown
            menuRef={mentions.menuRef}
            show={mentions.showMentionMenu}
            filteredFiles={mentions.filteredFiles}
            mentionFilter={mentions.mentionFilter}
            highlightIndex={mentions.mentionHighlight}
            onSelect={handleSelectFile}
            onHighlight={mentions.setMentionHighlight}
            position="above"
          />
        </div>

        <motion.button
          onClick={isProcessing ? onCancel : hasText ? handleSend : undefined}
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.9 }}
          transition={spring}
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
            "transition-colors duration-150",
            isProcessing
              ? "bg-[var(--color-soft-red)]/15 text-[var(--color-soft-red)]"
              : hasText
                ? "bg-[var(--text-primary)] text-[var(--text-inverse)]"
                : "bg-[var(--bg-surface-hover)] text-[var(--text-muted)] border border-[var(--border-hairline)]",
          )}
        >
          <AnimatePresence mode="wait" initial={false}>
            {isProcessing ? (
              <motion.div
                key="stop"
                initial={{ scale: 0.4, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.4, opacity: 0 }}
                transition={{ duration: 0.12 }}
              >
                <Stop size={12} weight="fill" />
              </motion.div>
            ) : hasText ? (
              <motion.div
                key="arrow"
                initial={{ scale: 0.5, opacity: 0, rotate: -90 }}
                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                exit={{ scale: 0.5, opacity: 0, rotate: 90 }}
                transition={{ duration: 0.12 }}
              >
                <ArrowUp size={14} weight="bold" />
              </motion.div>
            ) : (
              <motion.div
                key="mic"
                initial={{ scale: 0.5, opacity: 0, rotate: 90 }}
                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                exit={{ scale: 0.5, opacity: 0, rotate: -90 }}
                transition={{ duration: 0.12 }}
              >
                <Microphone size={14} weight="bold" />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>
      </div>
    </div>
  );
}
